// Example mount for the imported pathfinder (src/pathfinder/).
//
// Renders the standalone PathfinderTab full-screen against the static data
// snapshot copied to public/Map Info/pathfinder/ (manifest.json + <slug>.json +
// nav_<slug>.json + room_info.json + parking_lots.json). Route to this page
// however your app does routing, e.g. <PathfinderPage /> on /pathfinder.
//
// See src/pathfinder/INTEGRATION.md for wiring options + swapping the data
// source to your live /api/* endpoints.

import { useEffect, useMemo, useRef, useState } from 'react';
import PathfinderTab from './pathfinder/PathfinderTab.jsx';
import { createHttpDataSource } from './pathfinder/lib/dataSource.js';
import EventBody from './components/EventBody.jsx';
import { eventUrl } from './utils/eventSlug.js';
import { qrBadgeSvg, MARK_CSS_WIDTH, UMAPS_BADGE_URL } from './utils/qrBadge.js';
import { eventEnd, eventStart, fmtWhenShort, isFinished, pinLabel } from './utils/eventTime.js';
import { eventTags, eventWhere, isOffCampus, offCampusAddress, offCampusLink, ticketOf }
  from './utils/eventTags.js';
import { applyPlaceEdits } from './utils/placeEdits.js';
import { liveRoomNames } from './utils/liveRoomNames.js';
import AppDownloadPopup, { shouldShowAppPopup } from './components/AppDownloadPopup.jsx';
import StoreBadges from './components/StoreBadges.jsx';
import { detectOs } from './utils/appStores.js';

// Same Maps key the rest of UMapsWeb uses (CRA inlines REACT_APP_* at build).
const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;

// Optional VECTOR Map ID (Google Cloud → Create Map ID → JavaScript → Vector,
// Tilt + Rotation enabled). When present the map unlocks free rotation/tilt;
// without it the map stays north-up. Passed as a prop so the pathfinder folder
// stays copy-out clean (no process.env reads inside src/pathfinder/).
const MAPS_ID = process.env.REACT_APP_MAPS_ID || process.env.REACT_APP_MAPS_MAP_ID;

// In dev the API runs on the Express server (port 3001); in prod it's same-origin.
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

// Static snapshot served from /public. Swap for an /api adapter later if you'd
// rather serve the graph from your server (see INTEGRATION.md).
// NOTE: the folder name has a space ("Map Info"). It MUST be percent-encoded
// here — Safari/WebKit's fetch() rejects a raw space in the URL with
// "The string did not match the expected pattern." (Chrome silently encodes it,
// which is why it only broke on iOS/Safari). Keep it as %20.
// Photos (place pictures, intersection panoramas) are too big to ship in this
// repo, so they live in Vercel Blob and REACT_APP_IMAGES_BASE points at the
// store. They keep the exact relative paths places.json and the nav files
// already store, so switching between the two is a base URL and nothing else.
// Unset → falls back to /Map Info/buildingfiles/, which is where a local
// checkout serves them from.
const snapshotSource = createHttpDataSource('/Map%20Info/pathfinder/', {
  imagesBase: process.env.REACT_APP_IMAGES_BASE || undefined,
});

// Student-submitted photos an admin has accepted, keyed by place id. They can't
// live in the snapshot — that file is regenerated wholesale by the authoring
// tool — so they ride alongside it as an overlay the pathfinder merges in.
// Optional by design: if the endpoint is down, places just show their own
// photos. Wrapped around the snapshot source rather than added to it, so
// src/pathfinder/ stays free of anything UMapsWeb-specific.
const dataSource = {
  ...snapshotSource,
  // The authored places with the Content Filler's edits on top (hours a
  // volunteer filled in, a menu price that changed, a photo taken down) —
  // /api/place-edits, merged by the one pure function in utils/placeEdits.js.
  // ⚠️ Fetched ALONGSIDE the snapshot rather than after it, and allowed to
  // fail: an overlay that is down costs the edits, never the places.
  loadPlaces: async () => {
    const [places, edits] = await Promise.all([
      snapshotSource.loadPlaces(),
      fetch(`${API_BASE}/api/place-edits`)
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => j?.edits || null)
        .catch(() => null),
    ]);
    return applyPlaceEdits(places, edits);
  },
  // Friendly room names: the authored sheets plus what the operator published
  // from /volunteer/rooms (/api/room-names), merged by utils/roomNameOverlay.js
  // — the phone mirrors that file, so both clients call a room the same thing.
  loadRoomNames: liveRoomNames(snapshotSource, API_BASE),
  loadPlacePhotos: async () => {
    const r = await fetch(`${API_BASE}/api/place-photo`);
    if (!r.ok) return null;
    const j = await r.json();
    return j?.approved || null;
  },
  // Star ratings, same story: they change by the hour, so they can't live in a
  // snapshot. Absent or failing just means every spot reads "No rating".
  loadPlaceRatings: async () => {
    const r = await fetch(`${API_BASE}/api/place-rating`);
    if (!r.ok) return null;
    const j = await r.json();
    return j?.ratings || null;
  },
};

// One student's star rating → /api/place-rating. `previous` is what this
// browser rated the spot before (it remembers its own vote), so a correction
// replaces that vote instead of adding a second one. Awaited: the panel shows
// the new average the server comes back with.
async function ratePlace(placeId, stars, previous) {
  const r = await fetch(`${API_BASE}/api/place-rating`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ placeId, rating: stars, previous }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) throw new Error(j.error || "Couldn't save that rating.");
  return j;
}

// A visitor's photos of a place with none yet → /api/place-photo, where they
// wait in a moderation queue until accepted at /admin. Unlike the fire-and-
// forget logs this one is awaited: the panel tells the user whether it landed.
async function submitPlacePhotos(submission) {
  const r = await fetch(`${API_BASE}/api/place-photo`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ op: 'submit', ...submission }),
  });
  // A body over the platform's limit is rejected by the edge with an HTML page,
  // not our JSON — without this the user gets "that didn't send" and no idea why.
  const j = await r.json().catch(() => ({}));
  if (r.status === 413) throw new Error('That photo was too large to send. Try one taken at a smaller size.');
  if (!r.ok || !j.ok) throw new Error(j.error || "That didn't send. Try again in a moment.");
  return j;
}

// U of M campus — a sensible default center if geolocation isn't available yet.
const CAMPUS_CENTER = { lat: 49.8094, lng: -97.1337 };

// ── Anonymous usage dimensions ────────────────────────────────────────────────
// Raw search rows can only ever say "N searches happened". These three fields
// let them say "N students, this often, on a phone" — which is the number that
// makes the case to the university — and they are deliberately the weakest
// identifiers that answer that question at all:
//
//  · the session id is random, lives in sessionStorage, and dies with the tab.
//    It cannot follow anyone between visits, and the server keeps raw ids for
//    48h only (see api/_pathLog.js) — the durable history is counts.
//  · "returning" is one local date stamp read back as a boolean. No id is
//    stored, so nothing can be joined across visits server-side.
//  · the device class is 'mobile' or 'desktop'. Never the user-agent string.
//
// What is deliberately NOT sent: any position (the map uses your location, the
// log never sees it), IP beyond the transient rate-limit check, and anything
// tied to the collected email list — those stay two unlinked stores.
const SESSION_KEY = 'umaps-pf-session';
const FIRST_SEEN_KEY = 'umaps-pf-first-seen';

function randomId() {
  try {
    const a = new Uint8Array(8);
    crypto.getRandomValues(a);
    return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
  }
}

// Throwaway per-tab id. Storage can throw (Safari private mode, blocked
// cookies) — a search that can't be grouped still logs, it just isn't counted
// toward the unique-session tally.
function sessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) { id = randomId(); sessionStorage.setItem(SESSION_KEY, id); }
    return id;
  } catch { return null; }
}

// Has this browser been here on an earlier day? One date in, one boolean out.
function isReturning() {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const first = localStorage.getItem(FIRST_SEEN_KEY);
    if (!first) { localStorage.setItem(FIRST_SEEN_KEY, today); return false; }
    return first !== today;
  } catch { return false; }
}

const deviceClass = () =>
  (/Mobile|Android|iPhone|iPad|iPod/.test(navigator.userAgent || '') ? 'mobile' : 'desktop');

// Persist each route search (From → To + option toggles) to our DB. Fire-and-
// forget: logging must never interfere with the map, so failures are swallowed.
function logRouteSearch(entry) {
  try {
    const body = JSON.stringify({
      ...entry,
      session: sessionId(),
      returning: isReturning(),
      device: deviceClass(),
    });
    const url = `${API_BASE}/api/path-log`;
    // Prefer sendBeacon so a search logged right before navigation still lands.
    if (!API_BASE && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* never block the UI on logging */ }
}

// A scanned printed sign → /api/scan-log (review at /admin). Logged on arrival,
// before any destination exists, so the scans that never turn into a search are
// still counted — a sign scanned and abandoned is the one worth moving. Carries
// the same throwaway session id as a route search, which is what lets the admin
// view say where that scan went next.
function logScan(scan) {
  try {
    const body = JSON.stringify({
      ...scan, session: sessionId(), returning: isReturning(), device: deviceClass(),
    });
    const url = `${API_BASE}/api/scan-log`;
    // sendBeacon so a scan still lands if they close the tab without searching —
    // which is exactly the case this log exists to see.
    if (!API_BASE && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* never block the UI on logging */ }
}

// UManitoba email from the welcome / support popups → /api/collect-email
// (stored as its own list; review at /admin).
function logEmail(email) {
  try {
    fetch(`${API_BASE}/api/collect-email`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }), keepalive: true,
    }).catch(() => {});
  } catch { /* never block the UI on logging */ }
}

// "Nearby" panel interactions → /api/place-log (review at /admin). Which study
// spaces get looked at, how much of the demand is washrooms, and how often a
// filter comes back empty — the last one being a gap in campus provision rather
// than a bug. Carries the same throwaway session id as a route search so unique
// visitors can be counted, but the server drops it rather than storing it on
// the row: see api/_placeLog.js.
function logPlaceEvent(event) {
  try {
    fetch(`${API_BASE}/api/place-log`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...event, session: sessionId(), device: deviceClass() }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never block the UI on logging */ }
}

// Problem report + session snapshot → /api/report (review at /admin).
function logReport(report) {
  try {
    fetch(`${API_BASE}/api/report`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report), keepalive: true,
    }).catch(() => {});
  } catch { /* never block the UI on logging */ }
}

// ── Club events on the campus map ────────────────────────────────────────────
// Fetched here rather than inside src/pathfinder/, which is copy-out clean and
// knows nothing about this site's API. The pathfinder is handed a plain list
// and does the two things only it can: place each one on the map, and route to
// it. Everything about an event that is not a position — when it is, what the
// club wrote, where its own page lives — is decided in this file.
//
// A failure is silent on purpose. Events are a bonus on a page whose actual job
// is finding a room, and an error banner over the search box for a club event
// that could not load would be the tail wagging the dog.
function useCampusEvents() {
  const [raw, setRaw] = useState(null);
  useEffect(() => {
    let dead = false;
    fetch(`${API_BASE}/api/events`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!dead && d?.events) setRaw(d.events); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  // A coarse beat, so an event drops off the map when it ends and today's pin
  // stops saying a time that has passed — rather than on the next reload. A
  // minute, not a second: nothing here counts down.
  const [minute, setMinute] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setMinute(n => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // ⚠️ THE SAME ARRAY BACK WHEN NOTHING HAS CHANGED. This runs every minute,
  // and a fresh array identity would rebuild all forty map pins each time —
  // forty DOM overlays torn down and put back, which is a visible blink. The
  // signature covers everything the map draws from, so a real change (an event
  // ending, a label turning from a date into a time) still gets through.
  const stable = useRef({ sig: '', list: [] });
  return useMemo(() => {
    const now = Date.now();
    const list = (raw || [])
      .filter(e => !isFinished(e, now))
      .map(e => ({
        ...e,
        startsAt: eventStart(e)?.getTime() ?? null,
        endsAt: eventEnd(e)?.getTime() ?? null,
        when: pinLabel(e, now),          // the one line printed on the pin
        whenLong: fmtWhenShort(e),       // the sheet and the calendar rows
        url: eventUrl(e, undefined, raw || []),
        // The club's tags, already decided. The pathfinder draws them and does
        // not interpret them — the wording of "Engineering students only", the
        // shape of a price, and what counts as off campus are all this site's
        // vocabulary, and putting them inside that folder would be the first
        // thing in it that only makes sense at this university.
        where: eventWhere(e),
        tags: eventTags(e),
        ticket: ticketOf(e),
        // Off campus: no pin, no walking route, and a link out instead. The
        // address is already inside `where`; this is the thing to tap.
        offsite: isOffCampus(e)
          ? { url: offCampusLink(offCampusAddress(e)) }
          : null,
      }))
      .sort((a, b) => (a.startsAt ?? Infinity) - (b.startsAt ?? Infinity));
    const sig = list.map(e => `${e.id}|${e.when.text}|${e.when.tone}`).join(';');
    if (sig === stable.current.sig) return stable.current.list;
    stable.current = { sig, list };
    return list;
  // `minute` is what re-runs this as events start and end.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, minute]);
}

// The club's authored body — pictures, paragraphs, links — rendered by the same
// component the attendee page uses, so the two cannot drift into
// disagreeing about what an event looks like. Handed to the pathfinder as a
// render prop rather than imported inside it: see src/pathfinder/INTEGRATION.md.
const renderEventBody = (e) => <EventBody blocks={e.blocks} />;

// The bottom-centre slot, where "Nearby" used to be. The places, the filters
// and the photos that button opened are what the phone app is for, so this map
// hands the reader over to it: the two store badges, straight to the listings,
// the visitor's own store first — the same badges the download card shows.
// Drawn here, not in the pathfinder, because the store marks are ours.
const renderAppPromo = () => <StoreBadges first={detectOs()} className="sb-float" />;

/**
 * The route's QR, for the pill's ⌃ menu.
 *
 * Here rather than inside `src/pathfinder/` because the mark in the middle is
 * ours and that folder is copy-out clean — the same reason `renderEventBody`
 * and `renderAppPromo` are props. It is also the same `qrBadgeSvg` the printed
 * building signs and the event posters use, so a route someone scans off a
 * screen is visibly the same object as a sign on a wall: level M, the white
 * disc and accent ring drawn into the SVG, the mark laid over it.
 *
 * On screen only, so the mark stays a DOM `<img>` — nothing here leaves the
 * page, and a failed load hides itself rather than printing a broken image.
 */
const renderQr = ({ url, size }) => (
  <div style={{ position: 'relative', width: '100%' }}>
    <div style={{ lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: qrBadgeSvg(url, size) }} />
    <img
      src={UMAPS_BADGE_URL}
      alt=""
      onError={(e) => { e.target.style.display = 'none'; }}
      style={{
        position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        width: MARK_CSS_WIDTH, height: 'auto', display: 'block',
      }}
    />
  </div>
);

// ⚠️ THE FIRST-VISIT POPUP IS OFF (`enableFeedback={false}` below). Nothing
// here is shown to a visitor on arrival any more; what survives is the `href`,
// which the "?" uses for its donate button — a screen somebody opens on
// purpose. The title and blurb are kept so turning the ask back on is one word
// rather than a rewrite.
//
// The link is written out inside the sentence AND given a button. The sentence
// is what somebody reads to a friend or types on another device; the button is
// what they tap. https rather than the http it is usually written as: same
// page, one less redirect, and no plaintext hop on a page about payment.
const SUPPORT = {
  title: 'Please support UMaps',
  blurb:
    'Hi, UMaps is a project ran by a solo developer, and need help sustaining it '
    + 'for the semester. Please consider donating and giving what you can at '
    + 'https://buymeacoffee.com/myumaps, every little bit helps!',
  href: 'https://buymeacoffee.com/myumaps',
};

export default function PathfinderPage() {
  // Best-effort portrait lock. The manifest's "orientation": "portrait" handles
  // installed PWAs; this covers browsers that support the Screen Orientation API
  // (mostly Android/Chrome, and only when allowed). iOS Safari ignores it.
  useEffect(() => {
    try { window.screen?.orientation?.lock?.('portrait').catch(() => {}); } catch { /* unsupported */ }
  }, []);

  const events = useCampusEvents();

  // The "get the app" card (components/AppDownloadPopup.jsx): the one
  // interruption on this page, host-owned so the pathfinder stays copy-out
  // clean, and distinct from the pathfinder's feedback popup below, which
  // stays off. Decided once at mount; a dismissal is remembered for a week.
  const [appPopup, setAppPopup] = useState(() => shouldShowAppPopup());

  return (
    <div style={{ position: 'fixed', inset: 0 }}>
      {appPopup && <AppDownloadPopup onDismiss={() => setAppPopup(false)} />}
      <PathfinderTab
        apiKey={MAPS_KEY}
        mapId={MAPS_ID}
        dataSource={dataSource}
        defaultCenter={CAMPUS_CENTER}
        defaultZoom={16}
        onRouteLog={logRouteSearch}
        onScanLog={logScan}
        // ⚠️ THE PATHFINDER'S OWN POPUP STAYS OFF. `enableFeedback` is the only
        // thing that opens it. The one card a visitor sees is the host's
        // AppDownloadPopup above — two popups on one arrival would be the
        // interruption this flag was turned off to prevent.
        //
        // ⚠️ It is `enableFeedback`, and NOT dropping `support`, because those
        // are not the same thing: the popup falls back to the older Instagram +
        // email welcome when there is no `support.blurb`, so removing the ask on
        // its own would swap one interruption for another rather than removing
        // it. Flip this back to `true` to bring the donation ask straight back —
        // SUPPORT is kept whole for exactly that.
        enableFeedback={false}
        // Still passed: the "?" reads `support.href` for its donate button, and
        // that one is opened deliberately by somebody asking about the project
        // rather than shown to everybody who arrives.
        support={SUPPORT}
        onEmailCollect={logEmail}
        onReport={logReport}
        onPlaceLog={logPlaceEvent}
        // Photos of a place that has none; reviewed at /admin before they show.
        onPhotoSubmit={submitPlacePhotos}
        // Out-of-five stars on study spots and libraries.
        onPlaceRate={ratePlace}
        // What's on: pins for the coming week, and the Events calendar.
        events={events}
        renderEventBody={renderEventBody}
        // Top right of the Events calendar: the way in for the clubs who post.
        clubLogin={{ href: '/eventmapcreator', label: 'Club Login' }}
        renderAppPromo={renderAppPromo}
        renderQr={renderQr}
        notice="UMaps is not associated with the University of Manitoba, and some map data may be inaccurate. Use at your own risk."
      />
    </div>
  );
}
